import { type AxePurchaseState, type v2 } from '@deque/billing-service-client';
import {
  listProductAccess,
  type ProductAccess,
  resolveProductSubscription,
  type ScopedSubscription,
  type Subscription
} from '@deque/billing-utils';
import { ACTIVE_STATES, FREE_STATES, ProductSlugs } from '../constants';

interface HasAnyActiveSubscriptionParams {
  productAccess: Record<ProductSlugs, AxePurchaseState>;
  allowedSlugsWithFreeStates?: ProductSlugs[];
}

export const hasAnyActiveSubscription = ({
  productAccess,
  allowedSlugsWithFreeStates = []
}: HasAnyActiveSubscriptionParams) => {
  // Only check free states for slugs that are in the allowedSlugsWithFreeStates array
  if (allowedSlugsWithFreeStates.length) {
    const includesSlug = (slug: ProductSlugs) =>
      allowedSlugsWithFreeStates.includes(slug);

    return !!Object.entries(productAccess).find(([slug, state]) =>
      includesSlug(slug as ProductSlugs)
        ? FREE_STATES.includes(state) || ACTIVE_STATES.includes(state)
        : ACTIVE_STATES.includes(state)
    );
  }

  return !!Object.values(productAccess).find(state =>
    ACTIVE_STATES.includes(state)
  );
};

interface HasAccessToProductParams {
  productAccess: Record<ProductSlugs, AxePurchaseState>;
  slug: ProductSlugs;
  allowedSlugsWithFreeStates?: ProductSlugs[];
}

export const hasAccessToProduct = ({
  productAccess,
  slug,
  allowedSlugsWithFreeStates = []
}: HasAccessToProductParams) => {
  // Only check free states for slugs that are in the allowedSlugsWithFreeStates array
  if (allowedSlugsWithFreeStates.includes(slug)) {
    return (
      FREE_STATES.includes(productAccess[slug]) ||
      ACTIVE_STATES.includes(productAccess[slug])
    );
  }

  return ACTIVE_STATES.includes(productAccess[slug]);
};

/** `'all'` rather than `{ enterpriseId }`: only the keyword scopes are documented to include the user's own subscriptions. */
const withinEnterprise = (
  billingUser: v2.User,
  enterpriseId: string
): v2.User => ({
  ...billingUser,
  enterprises: billingUser.enterprises.filter(({ id }) => id === enterpriseId)
});

const withoutBundleProviders = (
  billingUser: v2.User,
  productSlug: ProductSlugs
): v2.User => ({
  ...billingUser,
  subscriptions: billingUser.subscriptions.filter(
    s => s.product_slug === productSlug
  ),
  enterprises: billingUser.enterprises.map(enterprise => ({
    ...enterprise,
    subscriptions: enterprise.subscriptions.filter(
      s => s.product_slug === productSlug
    )
  }))
});

/** Each product's controlling personal-or-seat subscription within `enterpriseId`, one row per product with bundles ignored. */
export const getControllingSubscriptions = (
  billingUser: v2.User,
  enterpriseId: string
): ScopedSubscription[] => {
  const scoped = withinEnterprise(billingUser, enterpriseId);

  return Object.values(ProductSlugs).flatMap(productSlug => {
    const controlling = resolveProductSubscription({
      entity: withoutBundleProviders(scoped, productSlug),
      productSlug,
      enterpriseScope: 'all'
    });

    return controlling ? [controlling] : [];
  });
};

/** The enterprise's rows, with the caller's personal row replacing a product's rows when the enterprise holds none, holds only `none`, or the caller's seat lost to it. */
const getEnterpriseViewSubscriptions = (
  billingUser: v2.User,
  enterprise: v2.Enterprise
): Subscription[] => {
  const callerSeatSubscriptionIds = new Set(
    billingUser.enterprises
      .find(({ id }) => id === enterprise.id)
      ?.subscriptions.map(({ id }) => id)
  );
  const personalByProduct = new Map(
    getControllingSubscriptions(billingUser, enterprise.id)
      .filter(({ source }) => source === 'user')
      .map(({ subscription }) => [subscription.product_slug, subscription])
  );
  const replacedProducts = new Set<string>();
  const enterpriseProducts = new Set(
    enterprise.subscriptions.map(({ product_slug }) => product_slug)
  );

  return [
    ...enterprise.subscriptions.flatMap(subscription => {
      const personal = personalByProduct.get(subscription.product_slug);
      const personalMayReplace =
        callerSeatSubscriptionIds.has(subscription.id) ||
        subscription.purchase_state === 'none';

      if (!personal || !personalMayReplace) {
        return [subscription];
      }

      if (replacedProducts.has(subscription.product_slug)) {
        return [];
      }

      replacedProducts.add(subscription.product_slug);
      return [personal];
    }),
    ...[...personalByProduct.values()].filter(
      ({ product_slug }) => !enterpriseProducts.has(product_slug)
    )
  ];
};

/** What an enterprise-wide view of `activeEnterprise` shows, or the user's own subscriptions when there is none. */
export const getVisibleSubscriptions = (
  billingUser: v2.User,
  activeEnterprise: v2.Enterprise | null | undefined
): Subscription[] =>
  activeEnterprise
    ? getEnterpriseViewSubscriptions(billingUser, activeEnterprise)
    : billingUser.subscriptions;

const getMemberViewSubscriptions = (
  billingUser: v2.User,
  enterprise: v2.Enterprise
): Subscription[] => {
  const controlling = getControllingSubscriptions(
    billingUser,
    enterprise.id
  ).map(({ subscription }) => subscription);
  const controlledProducts = new Set(
    controlling.map(({ product_slug }) => product_slug)
  );

  return [
    ...controlling,
    ...enterprise.subscriptions.filter(
      ({ product_slug }) => !controlledProducts.has(product_slug)
    )
  ];
};

/** What the viewer's own plan is decided from: for members the resolver's pick per product, with the enterprise's rows for products it has nothing for; for admins the enterprise-wide view. */
export const getViewerSubscriptions = (
  billingUser: v2.User,
  activeEnterprise: v2.Enterprise | null | undefined,
  isAdmin: boolean
): Subscription[] =>
  activeEnterprise && !isAdmin
    ? getMemberViewSubscriptions(billingUser, activeEnterprise)
    : getVisibleSubscriptions(billingUser, activeEnterprise);

/** Product access derived from `getViewerSubscriptions`. */
export const getViewerProductAccess = (
  billingUser: v2.User,
  activeEnterprise: v2.Enterprise | null | undefined,
  isAdmin: boolean
): Readonly<ProductAccess> =>
  listProductAccess(
    getViewerSubscriptions(billingUser, activeEnterprise, isAdmin)
  );
